import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import UserPatternSettingUpdate from "./_111_user_pattern_setting_update.js";
import { getCachedPatternData, setCachedPatternData } from "./patterns/cacheHelper.js";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { useDateRange } from "./dateRangeContext.js";
import { formatDateMMDD } from "./patterns/helpers.js";
import { logEvent } from "./eventLogger.js";
import "./styles.css";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

const MODULE_NAME = "97_over_trading";
const SCREEN = "pg_patterns";

const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    marginTop: 16,
    background: "#222",
};

const thStyle = {
    padding: "8px 12px",
    fontSize: 13,
    textAlign: "left",
    borderBottom: "1px solid #555",
    backgroundColor: "#333",
    fontWeight: 600,
    color: "#fff",
};

const tdStyle = {
    padding: "8px 12px",
    borderBottom: "1px solid #444",
    fontSize: 13,
    color: "#ccc",
};

const GearIcon = ({ size = 24, color = "#fff" }) => (
    <span
        style={{
            fontSize: size,
            color,
            verticalAlign: "middle",
            cursor: "pointer",
            userSelect: "none",
            lineHeight: 1,
            display: "inline-block",
        }}
        title="Settings"
        aria-label="Settings"
    >
        &#9881;
    </span>
);

const Pattern_over_trading = ({ userId: propUserId }) => {
    const { windowDays } = useDateRange();

    const storedId = sessionStorage.getItem("user_id");
    const userId = propUserId || (storedId && !isNaN(Number(storedId)) ? Number(storedId) : 1);

    const [date, setDate] = useState("");
    const [tooltip, setTooltip] = useState("");
    const [overTradingPattern, setOverTradingPattern] = useState(null);
    const [totalPL, setTotalPL] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [chartMode, setChartMode] = useState("cumulative");
    const [showSettings, setShowSettings] = useState(false);
    const [patternId, setPatternId] = useState(null);
    const [patternConfig, setPatternConfig] = useState(null);
    const [fromCache, setFromCache] = useState(false);
    const [chartKey, setChartKey] = useState(0);
    const [showTable, setShowTable] = useState(false);
    const chartContainerRef = useRef(null);

    function getLookbackDays(val) {
        if (!val) return 30;
        if (!isNaN(Number(val))) return Number(val);
        if (val === "YTD") return 365;
        return 30;
    }

    const fetchPatternData = async (days) => {
        setLoading(true);

        try {
            const userKey = sessionStorage.getItem("userKey");
            const fetchParams = {
                lookback_days: days
            };

            if (userKey) {
                fetchParams.userKey = userKey;
            }

            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/_97_pattern_over_trading`,
                {
                    params: fetchParams,
                    withCredentials: true,
                }
            );

            console.log("[_97_pattern_over_trading] Full API Response:", res.data);
            console.log("[_97_pattern_over_trading] pl_matches:", res.data.pl_matches);
            if (res.data.pl_matches && res.data.pl_matches.length > 0) {
                console.log("[_97_pattern_over_trading] First match sample:", res.data.pl_matches[0]);
            }

            setDate(res.data.date);
            setTooltip(res.data.tooltip);
            setOverTradingPattern(res.data);
            setTotalPL(res.data.total_pl);

            const numericPatternId = res.data.pattern_id;
            setPatternId(numericPatternId);
            setPatternConfig(res.data.config || {});

            if (numericPatternId) {
                await setCachedPatternData(
                    `pattern_${numericPatternId}`,
                    days,
                    res.data
                );
            }

            setFromCache(false);
            setChartKey(prev => prev + 1);
        } catch (err) {
            console.error("Error fetching pattern data:", err);

            if (patternId) {
                const cachedData = await getCachedPatternData(
                    `pattern_${patternId}`,
                    days
                );
                if (cachedData) {
                    setDate(cachedData.date);
                    setTooltip(cachedData.tooltip);
                    setOverTradingPattern(cachedData);
                    setTotalPL(cachedData.total_pl);
                    setPatternConfig(cachedData.config || {});
                    setFromCache(true);
                    setChartKey(prev => prev + 1);
                    setLoading(false);
                    return;
                }
            }

            setTooltip("Error fetching data.");
            setOverTradingPattern(null);
            setTotalPL(null);
        }

        setLoading(false);
    };

    useEffect(() => {
        const lookback = getLookbackDays(windowDays);
        if (lookback) {
            fetchPatternData(lookback);
        }
        // eslint-disable-next-line
    }, [windowDays]);

    const tooltipIcon = tooltip ? (
        <span
            className="tooltip-icon"
            style={{
                marginLeft: 6,
                verticalAlign: "middle",
                cursor: "pointer",
                fontSize: "1.1em",
            }}
            title={tooltip}
        >
            🛈
        </span>
    ) : null;

    const calculateRollingAverage = (data, key, window = 14) => {
        return data.map((point, idx) => {
            const start = Math.max(0, idx - window + 1);
            const slice = data.slice(start, idx + 1);
            const avg = slice.reduce((sum, p) => sum + p[key], 0) / slice.length;
            return avg;
        });
    };

    const getYearLabels = (data) => {
        const yearLabels = [];
        let lastYear = null;
        data.forEach((d, idx) => {
            const year = new Date(d.date).getFullYear();
            if (year !== lastYear) {
                yearLabels.push({ year, idx });
                lastYear = year;
            }
        });
        return yearLabels;
    };

    const timeEvolution = overTradingPattern?.time_evolution || [];
    const chartLabels = timeEvolution.map(d => formatDateMMDD(d.date));
    const yearLabels = getYearLabels(timeEvolution);

    const overTradingMatches = overTradingPattern?.pl_matches || [];

    const buildChartData = () => {
        if (!timeEvolution || timeEvolution.length === 0) {
            return { labels: [], datasets: [] };
        }

        const labels = timeEvolution.map(d => formatDateMMDD(d.date));
        const frequency = timeEvolution.map(d => d.frequency || 0);
        const cumulativePl = timeEvolution.map(d => d.cumulative_pl || 0);

        let datasets = [];

        if (chartMode === "cumulative") {
            datasets = [
                {
                    type: "line",
                    label: "Cumulative P/L",
                    data: cumulativePl,
                    borderColor: "#00FFFF",
                    backgroundColor: "rgba(0, 255, 255, 0.1)",
                    borderWidth: 3,
                    pointRadius: 4,
                    pointBackgroundColor: "#00FFFF",
                    fill: true,
                    tension: 0.3,
                    yAxisID: "y",
                },
                {
                    type: "bar",
                    label: "Overtrading Intensity (Trades)",
                    data: frequency,
                    backgroundColor: "rgba(76, 175, 80, 0.3)",
                    borderColor: "#4CAF50",
                    borderWidth: 1,
                    yAxisID: "y2",
                },
            ];
        } else if (chartMode === "rolling") {
            const rollingPl = calculateRollingAverage(timeEvolution, "cumulative_pl", 14);
            datasets = [
                {
                    type: "line",
                    label: "14-Day Rolling Avg P/L",
                    data: rollingPl,
                    borderColor: "#FF6B9D",
                    backgroundColor: "rgba(255, 107, 157, 0.1)",
                    borderWidth: 3,
                    pointRadius: 4,
                    pointBackgroundColor: "#FF6B9D",
                    fill: true,
                    tension: 0.3,
                    yAxisID: "y",
                },
                {
                    type: "bar",
                    label: "Overtrading Intensity (Trades)",
                    data: frequency,
                    backgroundColor: "rgba(76, 175, 80, 0.3)",
                    borderColor: "#4CAF50",
                    borderWidth: 1,
                    yAxisID: "y2",
                },
            ];
        } else if (chartMode === "tradeByTrade") {
            datasets = [
                {
                    type: "bar",
                    label: "Frequency",
                    data: frequency,
                    backgroundColor: "rgba(76, 175, 80, 0.6)",
                    yAxisID: "y2",
                },
                {
                    type: "line",
                    label: "Cumulative P/L",
                    data: cumulativePl,
                    borderColor: "#00FFFF",
                    borderWidth: 2,
                    fill: false,
                    tension: 0.3,
                    yAxisID: "y",
                    pointRadius: 4,
                },
            ];
        }

        return { labels, datasets };
    };

    const chartData = buildChartData();
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true, position: "top" },
            title: { display: true, text: "Overtrading Pattern P/L Evolution" },
        },
        scales: {
            y: {
                type: "linear",
                position: "left",
                title: { display: true, text: "P/L ($)" },
                ticks: { color: "#fff" },
                grid: { color: "#444" },
            },
            y2: {
                type: "linear",
                position: "right",
                title: { display: true, text: "Trades Index" },
                ticks: { color: "#fff" },
                grid: { drawOnChartArea: false },
                offset: true,
            },
            x: {
                ticks: { color: "#fff" },
                grid: { color: "#444" },
            },
        },
    };

    const renderSettingsModal = () => {
        if (!showSettings || !patternId) return null;

        return (
            <>
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(24,26,37,0.5)",
                        backdropFilter: "blur(6px)",
                        zIndex: 9998,
                    }}
                    onClick={() => setShowSettings(false)}
                />
                <div
                    style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 9999,
                        minWidth: 600,
                        minHeight: 420,
                        background: "#181A25",
                        borderRadius: 12,
                        boxShadow: "0 2px 8px rgba(0,255,255,0.08)",
                        padding: 0,
                    }}
                >
                    <UserPatternSettingUpdate
                        patternId={patternId}
                        countThreshold={patternConfig?.count_threshold}
                        thresholdDefinition={patternConfig?.threshold_definition}
                    />
                    <button
                        onClick={() => setShowSettings(false)}
                        style={{
                            position: "absolute",
                            top: 12,
                            right: 18,
                            background: "none",
                            border: "none",
                            color: "#fff",
                            fontSize: 22,
                            cursor: "pointer",
                        }}
                        title="Close"
                    >
                        ✕
                    </button>
                </div>
            </>
        );
    };

    const renderDataTable = () => {
        if (!overTradingMatches || overTradingMatches.length === 0) {
            return <div style={{ color: "#888", padding: 16 }}>No overtrading event data available.</div>;
        }

        return (
            <div style={{ marginTop: 16 }}>
                <div style={{ marginBottom: 12 }}>
                    <button
                        onClick={() => setShowTable(!showTable)}
                        style={{
                            padding: "8px 16px",
                            borderRadius: 6,
                            border: "1px solid #888",
                            background: showTable ? "#1976d2" : "#333",
                            color: "#fff",
                            fontWeight: 500,
                            cursor: "pointer",
                            transition: "background 0.2s",
                            fontSize: "0.98em",
                        }}
                    >
                        {showTable ? "Hide Trade Details" : "Show Trade Details"} ({overTradingMatches.length} events)
                    </button>
                </div>

                {showTable && (
                    <div style={{ overflowX: "auto" }}>
                        {overTradingMatches.length >= 500 && (
                            <div style={{
                                color: "#FFD700",
                                fontSize: "0.9em",
                                marginBottom: 8,
                                padding: "6px 12px",
                                background: "rgba(255, 215, 0, 0.1)",
                                borderRadius: 4
                            }}>
                                ⚠️ Showing first 500 records of {overTradingMatches.length} total matches
                            </div>
                        )}
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Date</th>
                                    <th style={thStyle}>Symbol</th>
                                    <th style={thStyle}>Frequency</th>
                                    <th style={thStyle}>Avg Trades</th>
                                    <th style={thStyle}>Daily P/L</th>
                                </tr>
                            </thead>
                            <tbody>
                                {overTradingMatches.slice(0, 500).map((event, idx) => {
                                    const eventDate = event.event_date || event.closing_date || event.date;

                                    return (
                                        <tr key={idx}>
                                            <td style={tdStyle}>
                                                {formatDateMMDD(eventDate)}
                                            </td>
                                            <td style={tdStyle}>
                                                <strong>{event.symbol || "—"}</strong>
                                            </td>
                                            <td style={tdStyle}>
                                                {event.frequency || "—"}
                                            </td>
                                            <td style={tdStyle}>
                                                {event.avg_trades ? event.avg_trades.toFixed(2) : "—"}
                                            </td>
                                            <td style={{
                                                ...tdStyle,
                                                color: event.pl >= 0 ? "#4caf50" : "#e53935",
                                                fontWeight: 600,
                                            }}>
                                                ${event.pl ? event.pl.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                }) : "0.00"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan="4" style={{
                                        ...tdStyle,
                                        fontWeight: 600,
                                        backgroundColor: "#2a2d38",
                                        textAlign: "right"
                                    }}>
                                        Total:
                                    </td>
                                    <td style={{
                                        ...tdStyle,
                                        fontWeight: 600,
                                        backgroundColor: "#2a2d38",
                                        color: overTradingMatches.reduce((sum, t) => sum + (t.pl || 0), 0) >= 0 ? "#4caf50" : "#e53935",
                                    }}>
                                        ${overTradingMatches.reduce((sum, t) => sum + (t.pl || 0), 0).toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        })}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    const renderPatternSummary = () => {
        if (!overTradingPattern) {
            return <span>No overtrading events in selected range.</span>;
        }

        const {
            pattern_label,
            pattern_meaning,
            pattern_matches,
            config,
        } = overTradingPattern;

        return (
            <div>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: 12,
                        flexWrap: "wrap",
                        gap: 12,
                    }}
                >
                    <span style={{ color: "#888", fontSize: "0.95em" }}>
                        {date && `Report Date: ${date}`}
                    </span>
                </div>

                {typeof totalPL === "number" && (
                    <div
                        style={{
                            marginTop: 6,
                            color: "#e0b400",
                            fontSize: "1.1em",
                            marginBottom: 12,
                        }}
                    >
                        <b>P/L:</b>{" "}
                        <span
                            style={{
                                color: totalPL >= 0 ? "#4caf50" : "#e53935",
                            }}
                        >
                            ${totalPL.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}
                        </span>
                    </div>
                )}

                {pattern_label && (
                    <div style={{ marginBottom: 6, color: "#1976d2", fontWeight: 600 }}>
                        {pattern_label}: {pattern_meaning}
                    </div>
                )}
                <div style={{ color: "#555", fontSize: "0.98em", marginBottom: 6 }}>
                    Total Violations: {pattern_matches}
                    {config?.threshold_definition?.threshold_1?.value && (
                        <>
                            <br />
                            <b>Trigger:</b>{" "}
                            {Math.round(config.threshold_definition.threshold_1.value * 100)}%
                            over daily average
                        </>
                    )}
                </div>
                <div style={{ margin: "8px 0" }}>
                    <a
                        href="#"
                        style={{
                            color: "#4fa3f7",
                            textDecoration: "underline",
                            cursor: "pointer",
                            fontSize: "0.97em",
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            setShowDetails((prev) => !prev);
                        }}
                    >
                        {showDetails ? "Hide details" : "More details"}
                    </a>
                    {showDetails && (
                        <div
                            style={{
                                marginTop: 6,
                                color: "#aaa",
                                fontSize: "0.97em",
                                borderLeft: "2px solid #444",
                                paddingLeft: 10,
                            }}
                        >
                            <div><b>Detections:</b> {timeEvolution.length} days flagged</div>
                            {timeEvolution.length > 0 && (
                                <div>
                                    <b>Latest Avg:</b>{" "}
                                    {timeEvolution[timeEvolution.length - 1].avg_trades || 0}
                                    trades/day
                                </div>
                            )}
                            {fromCache && (
                                <div style={{ marginTop: 6, fontSize: "0.9em", color: "#888" }}>
                                    📦 Data loaded from cache
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderChartToggles = () => (
        <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
            {["cumulative", "rolling", "tradeByTrade"].map((mode) => (
                <div key={mode} data-analytics={MODULE_NAME}>
                    <button
                        className={`btn ${chartMode === mode ? "btn-selected" : ""}`}
                        onClick={() => {
                            logEvent({
                                event_type: "button_click",
                                user_id: userId,
                                module_name: MODULE_NAME,
                                subcomponent: mode === "cumulative" ? "Cumulative Chart" : mode === "rolling" ? "Rolling Avg Chart" : "Event Scatter Chart",
                                screen: SCREEN,
                                metadata: {
                                    button_link_value: mode === "cumulative" ? "Cumulative" : mode === "rolling" ? "Rolling Avg" : "Event Scatter",
                                },
                            });
                            setChartMode(mode);
                        }}
                        style={{
                            padding: "6px 14px",
                            borderRadius: 6,
                            border: "1px solid #888",
                            background: chartMode === mode ? "#1976d2" : "#f5f5f5",
                            color: chartMode === mode ? "#fff" : "#222",
                            fontWeight: 500,
                            cursor: "pointer",
                            transition: "background 0.2s",
                            fontSize: "0.98em",
                        }}
                    >
                        {mode === "cumulative" ? "Cumulative" : mode === "rolling" ? "Rolling Avg" : "Event Scatter"}
                    </button>
                </div>
            ))}
        </div>
    );

    return (
        <div
            ref={chartContainerRef}
            style={{
                width: "100%",
                padding: 0,
                position: "relative",
            }}
        >
            <h3
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: "1.25em",
                    margin: "0 0 8px 0",
                    color: "#fff",
                }}
            >
                {overTradingPattern?.pattern_name || "Overtrading Pattern"} {tooltipIcon}
                <span
                    style={{ marginLeft: 8 }}
                    title="Edit pattern settings"
                    onClick={() => setShowSettings(true)}
                >
                    <GearIcon size={24} color="#fff" />
                </span>
            </h3>
            {renderSettingsModal()}
            <div style={{ minHeight: 32, fontSize: "1.05em", marginBottom: 16 }}>
                {loading ? <span>Loading...</span> : renderPatternSummary()}
            </div>

            {chartData.datasets.length > 0 && renderChartToggles()}

            <div style={{ position: "relative", width: "100%" }}>
                {chartData.datasets.length > 0 ? (
                    <>
                        <div
                            style={{
                                marginTop: 8,
                                marginBottom: 0,
                                width: "100%",
                                minHeight: 320,
                            }}
                        >
                            <Chart key={chartKey} data={chartData} options={chartOptions} />
                        </div>
                        {yearLabels.length > 1 && (
                            <div
                                style={{
                                    pointerEvents: "none",
                                    position: "absolute",
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    height: 24,
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "flex-start",
                                    zIndex: 2,
                                    width: "100%",
                                    fontWeight: 600,
                                    fontSize: "1em",
                                    color: "#222",
                                    userSelect: "none",
                                    textShadow: "0 1px 2px #fff, 0 0 2px #fff",
                                }}
                            >
                                {yearLabels.map((yl, i) => {
                                    const nextIdx = yearLabels[i + 1]?.idx ?? chartLabels.length;
                                    const widthPercent = ((nextIdx - yl.idx) / chartLabels.length) * 100;
                                    return (
                                        <div
                                            key={yl.year}
                                            style={{
                                                width: `${widthPercent}%`,
                                                textAlign: "center",
                                                position: "relative",
                                            }}
                                        >
                                            {yl.year}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                ) : (
                    !loading && (
                        <div style={{ color: "#888", padding: "40px", textAlign: "center", fontSize: "1.05em", border: "1px dashed #444" }}>
                            No chart data found for the current lookback.
                        </div>
                    )
                )}
            </div>

            {renderDataTable()}
        </div>
    );
};

export default Pattern_over_trading;